import React, {useCallback} from 'react';
import './RepProfilePageTabs.styl';
import DashboardBackButton from 'components/dashboard/DashboardBackButton';
import AssumedUserBadge from 'components/dashboard/AssumedUserBadge';
import {RepTab} from './RepTab';
import User from 'records/User';
import {CoachingDirection, RepProfileTab} from '@store/coaching-plans';

import {BullseyeIcon} from '@components/icon/BullseyeIcon';
import {featureFlags} from 'utils/const';
import {RepSubtab} from './RepSubtab';
import {MemoIcon} from '@components/icon/MemoIcon';
import {PhoneIcon} from '@components/icon/PhoneIcon';
import {ScorecardIcon} from '@components/icon/ScorecardIcon';
import {ChartLineUpIcon} from '@components/icon/ChartLineUpIcon';
import {OverlappingCommentsIcon} from '@components/icon/OverlappingCommentsIcon';

interface RepProfilePageTabsProps {
  selectedTabIndex: RepProfileTab;
  direction: CoachingDirection;
  rep: User;
  authUser: User;
  startDate;
  endDate;
  onSelectTabIndex;
  changeDirection;
  userHasFlag;
}

interface Tab {
  icon: any;
  label: string;
  tab: RepProfileTab;
  subtabs?: RepSubtab[];
}
const getTabs = (changeDirection, rep, userHasFlag): Tab[] => {
  const getSubtab = (label, subtabDirection): RepSubtab => ({
    label,
    onClick: () => changeDirection(subtabDirection),
    active: dir => (subtabDirection === dir ? 'active' : ''),
  });

  return [
    {
      icon: MemoIcon,
      label: 'Summary',
      tab: RepProfileTab.SUMMARY,
    },
    {
      icon: PhoneIcon,
      label: 'Call Activity',
      tab: RepProfileTab.CALL_ACTIVITY,
    },
    {
      icon: ScorecardIcon,
      label: 'Review Activities',
      tab: RepProfileTab.REVIEW_ACTIVITIES,
      subtabs: [
        getSubtab('Self review', CoachingDirection.review),
        getSubtab('Other review', CoachingDirection.coach),
      ],
    },
    {
      icon: ChartLineUpIcon,
      label: 'Coaching Plans',
      tab: RepProfileTab.COACHING_PLANS,
    },
    {
      icon: OverlappingCommentsIcon,
      label: 'Coaching Received',
      tab: RepProfileTab.COACHING_RECEIVED,
      subtabs:
        rep?.role !== 'manager'
          ? [
              getSubtab('From manager', CoachingDirection.manager),
              getSubtab('From other', CoachingDirection.other),
            ]
          : undefined,
    },
    userHasFlag(featureFlags.adminKpis)
      ? {
          icon: BullseyeIcon,
          label: 'KPIs',
          tab: RepProfileTab.KPI,
        }
      : null,
    {
      icon: PhoneIcon,
      label: 'Calls',
      tab: RepProfileTab.CALLS,
    },
  ].filter(x => x) as Tab[];
};

export const RepProfilePageTabs: React.FC<RepProfilePageTabsProps> = ({
  authUser,
  changeDirection,
  direction,
  endDate,
  onSelectTabIndex,
  rep,
  selectedTabIndex,
  startDate,
  userHasFlag,
}) => {
  const onChangeDirection = useCallback(
    direction => {
      changeDirection(direction, rep?.teamId, rep?.id, startDate, endDate);
    },
    [rep, startDate, endDate, changeDirection]
  );

  return (
    <div className="admin-app-sidebar rep-profile-tabs">
      <DashboardBackButton />
      {rep?.id !== authUser.id && <AssumedUserBadge type="rep" user={rep} />}
      <div className="menu">
        {getTabs(onChangeDirection, rep, userHasFlag).map(tabProps => (
          <RepTab
            key={tabProps.tab}
            {...tabProps}
            direction={direction}
            activeTab={selectedTabIndex}
            onSelect={onSelectTabIndex}
          />
        ))}
      </div>
    </div>
  );
};
